import { createReducer } from 'typesafe-actions'
import { ID, IDRecord, } from '../../../types/UtilityTypes'
import * as A from 'fp-ts/es6/Array'
import * as R from 'fp-ts/es6/Record'
import { flow } from 'fp-ts/es6/function'
import { pipe } from 'fp-ts/es6/pipeable'
import { normalizeObject, normalizeObjects } from '../../../utils/normalize'
import { ServerProject } from '../../../responses/projects'
import { ObjectRecord, ordObject, ProjectWorkObject } from '../../../responses/objects'
import { combineReducers } from 'redux'
import { departmentsTasks, objectLens, } from '../../../utils/lenses'
import { taskListActions } from '../../pages/task-list/task-list-actions'
import { projectActions } from '../project/project-actions'
import { objectActions } from './object-actions'
import { taskActions } from '../task/task-actions'
import { modifyProp } from '../../../utils/utils'
import { getId, idRemove } from '../../../utils/entity-utils'
import { RequestStatus } from '../../../responses/response'
import { statusReducer } from '../../../utils/redux-utils'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'

const departmentsObjects = flow(departmentsTasks, A.map(objectLens.get))

export const ObjectEditState = makeADT('type')({
    none: ofType<{ type: 'none' }>(),
    close: ofType<{ type: 'close', object: ProjectWorkObject }>(),
    delete: ofType<{ type: 'delete', object: ProjectWorkObject }>(),
    update: ofType<{ type: 'update', object: ProjectWorkObject }>(),
    create: ofType<{ type: 'create', projectId: ID }>(),
    rename: ofType<{ type: 'rename', object: ProjectWorkObject }>(),
    duplicate: ofType<{ type: 'duplicate', object: ProjectWorkObject }>(),
})

export interface ObjectState {
    editState: ADTType<typeof ObjectEditState>,
    closeStatus: RequestStatus,
    byId: ObjectRecord,
    byProject: IDRecord,
}

const prepareObjects = flow(
    A.sort(ordObject),
    A.map(getId),
)

export const objectReducer = combineReducers<ObjectState>({
    editState: createReducer<ADTType<typeof ObjectEditState>>(ObjectEditState.as.none({}))
        .handleAction(objectActions.startRename, (state, {payload}) => ObjectEditState.as.rename({object: payload}))
        .handleAction([objectActions.cancelEdit, objectActions.close.success], () => ObjectEditState.as.none({}))
        .handleAction(objectActions.startEdit, (_, {payload}) => ObjectEditState.as.update({object: payload}))
        .handleAction(objectActions.startDelete, (_, {payload}) => ObjectEditState.as.delete({object: payload}))
        .handleAction(objectActions.startCreate, (_, {payload}) => ObjectEditState.as.create({projectId: payload}))
        .handleAction(objectActions.startClose, (_, {payload}) => ObjectEditState.as.close({object: payload}))
        .handleAction(objectActions.startDuplicate, (_, {payload}) => ObjectEditState.as.duplicate({object: payload}))
        .handleAction(objectActions.update.success, (state, action) =>
            action.meta === 'user' && ObjectEditState.isAnyOf(['close', 'update', 'rename'])(state) && state.object.id === action.payload.id ?
                ObjectEditState.as.none({}) :
                state
        )
        .handleAction(projectActions.update.success, (state, action) =>
            ObjectEditState.is.create(state) && state.projectId == action.payload.id && action.meta === 'user' ?
                ObjectEditState.as.none({}) :
                state
        ),
    closeStatus: statusReducer(objectActions.close),
    byId: createReducer<ObjectRecord>({})
        .handleAction([
            projectActions.get.success,
            projectActions.create.success,
            projectActions.update.success
        ], (state, action) => normalizeObjects(action.payload.project_work_objects)(state))
        .handleAction(taskActions.requested.success, (state, action) =>
            normalizeObjects(action.payload.map(objectLens.get))(state))
        .handleAction(taskActions.user.success, (state, action) =>
            normalizeObjects(action.payload.tasks.map(objectLens.get))(state))
        .handleAction([projectActions.list.success, projectActions.archive.success], (state, action) =>
            normalizeObjects(action.payload.projects.flatMap((project: ServerProject) => project.project_work_objects))(state))
        .handleAction(taskActions.get.success, (state, action) =>
            modifyProp<ObjectRecord, ID>(action.payload.project_work_object_id, normalizeObject(action.payload.project_work_object))(state))
        .handleAction([objectActions.update.success, objectActions.close.success], (state, action) =>
            modifyProp<ObjectRecord, ID>(action.payload.id, normalizeObject(action.payload))(state))
        .handleAction(taskListActions.departments.success, (state, action) =>
            normalizeObjects(departmentsObjects(action.payload))(state)),
    byProject: createReducer<IDRecord>({})
        .handleAction([projectActions.get.success, projectActions.update.success], (state, action) => ({
            ...state,
            [action.payload.id]: prepareObjects(action.payload.project_work_objects),
        }))
        .handleAction(projectActions.list.success, (state, action) => pipe(
            action.payload.projects,
            A.reduce(state, (state, project: ServerProject) => ({
                ...state,
                [project.id]: prepareObjects(project.project_work_objects),
            }))
        ))
        .handleAction(objectActions.remove.success, (state, action) => pipe(state, R.map(idRemove(action.payload)))),
})

